import { Box, Tab, Tabs } from '@mui/material';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CloudFunctionApi } from '../../../cloudfunctions';
import { UserDetails } from '../../../constants/Common';
import {
	replaceHyphenWithSpace,
	replaceSpaceWithHyphen,
} from '../../../constants/stringUtilities';
import {
	isWorkbenchAccountType,
	WorkbenchAccountType,
	WorkbenchConfig,
} from '../../../constants/Workbench';
import type { User } from '../../../firebase/firebase';
import { useAbortController } from '../../../hooks/useAbortController';
import {
	IntegrationStatus,
	ProjectTrackingIntegration,
} from '../../../models/Integrations/ProjectTrackingIntegration';
import { StepperHeader } from '../../StepperHeader/StepperHeader';

export type WorkbenchTabsProps = {
	userDetails: UserDetails;
	user: User;
	integration: ProjectTrackingIntegration;
	currentTab: string;
	cloudFunctionApi: Pick<
		CloudFunctionApi,
		'updateProjectTrackingIntegrationStatus'
	>;
};

export const WorkbenchTabs = ({
	user,
	userDetails,
	integration,
	currentTab,
	cloudFunctionApi,
}: WorkbenchTabsProps): JSX.Element => {
	if (!isWorkbenchAccountType(userDetails.accountType)) {
		throw new Error(
			`Unauthorised. Account type ${userDetails.accountType} cannot access workbench.`,
		);
	}
	const abortSignal = useAbortController();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);

	const titles = WorkbenchConfig[userDetails.accountType].titles;

	const currentTabIndex = titles.indexOf(replaceHyphenWithSpace(currentTab));

	const stepperHeader = (accountType: WorkbenchAccountType): JSX.Element => {
		const stepperTabs = WorkbenchConfig[accountType].setupStepperTabs();

		const handleNext = (): void => {
			navigate(replaceSpaceWithHyphen(titles[currentTabIndex + 1]));
		};

		const handleBack = (): void => {
			navigate(replaceSpaceWithHyphen(titles[currentTabIndex - 1]));
		};

		const handleFinalize = async (): Promise<void> => {
			setLoading(true);
			await cloudFunctionApi.updateProjectTrackingIntegrationStatus(
				abortSignal,
				user,
				IntegrationStatus.Active,
			);
			setLoading(false);
		};
		return (
			<StepperHeader
				currentTab={currentTab}
				tabs={stepperTabs}
				next={handleNext}
				back={handleBack}
				finalize={handleFinalize}
				loading={loading}
			/>
		);
	};

	return integration.status === IntegrationStatus.Incomplete ? (
		stepperHeader(userDetails.accountType)
	) : (
		<Box
			sx={{
				borderBottom: 1,
				borderColor: 'divider',
			}}>
			<Tabs
				variant="scrollable"
				scrollButtons="auto"
				value={replaceHyphenWithSpace(currentTab)}>
				{titles.map((title, index) => (
					<Tab
						label={title}
						value={title}
						key={index}
						component={Link}
						to={replaceSpaceWithHyphen(title)}
					/>
				))}
			</Tabs>
		</Box>
	);
};
